// This is used as a base for both applications.

import { validationMixin } from "vuelidate";
import axios from "axios";
import { mapState } from "vuex";

import globals from "../../lib/globals";
import unique from "../../lib/unique";
import applicationReset from "../../lib/applicationReset";
import utils from "./utils";
import { extendApplicantCreator } from "../../lib/applicationShared";
import ApplicationBase from "../components/ApplicationBase";
import ApplicationApartments from "../components/ApplicationApartments";
import ApplicationApplicants from "../components/ApplicationApplicants";
import ApplicationSection from "../components/ApplicationSection";
import ApplicationDivider from "../components/ApplicationDivider";
import ApplicationOtherResidents from "../components/ApplicationOtherResidents";
import InputWrap from "../components/InputWrap";
import InputText from "../components/InputText";
import InputCheckbox from "../components/InputCheckbox";
import InputDate from "../components/InputDate";
import InputSelect from "../components/InputSelect";
import InputTextarea from "../components/InputTextarea";

// Helper for calling extend stuff (that may or may not be defined).
const createExtend =
  (extensions) =>
  (name, ...args) =>
    extensions.hasOwnProperty(name) ? extensions[name](...args) : {};
let hasListeners = false;

export default ({
  favouritesType,
  presetMapExtend,
  localSaveExtend,
  applyOtherData,
}) => ({
  mixins: [utils.translate, validationMixin],
  components: {
    InputSelect,
    InputCheckbox,
    InputWrap,
    InputTextarea,
    InputText,
    InputDate,
    ApplicationOtherResidents,
    ApplicationApplicants,
    ApplicationApartments,
    ApplicationSection,
    ApplicationDivider,
    ApplicationBase,
  },
  props: {
    heroImage: {
      type: String,
      required: true,
    },
    heroes: {
      type: Object,
      required: true,
    },
    textBefore: {
      type: String,
      default: "",
    },
    textAfter: {
      type: String,
      default: "",
    },
    warningApartmentsBefore: {
      type: String,
      default: "",
    },
    warningApartmentsAfter: {
      type: String,
      default: "",
    },
    successContent: {
      type: String,
      default: "",
    },
    successContentApply: {
      type: String,
      default: "",
    },
    modalLoadPresetEnabled: {
      type: Boolean,
      default: false,
    },
    modalLoadPresetTitle: {
      type: String,
      default: "",
    },
    modalLoadPresetContent: {
      type: String,
      default: "",
    },
    modalLoadPresetClose: {
      type: String,
      default: "",
    },
    preset: {
      type: Object,
      default: null,
    },
    disableFormFromWordPress: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      maxApartments: 100, // dunno
      enableLocalStorageUpdate: false,
      view: "form",
      submitting: false,
      deactivating: false,
      submitError: false,
      deactivateError: false,
      showModal: {
        loadPreset: false,
        confirmReset: false,
        prefillApplication: false,
        confirmDeactivation: false,
      },
      modalLoading: {
        prefillApplication: false,
      },
      modalError: {
        prefillApplication: false,
      },
      modalErrorMessages: {
        prefillApplication: "",
      },
      appliedApartments: [],
      applicants: [this.createApplicant()],
      existingApplicantIndexes: {},
      form: {
        Id: null,
        OtherResidents: [],
        AdditionalWishes: "",
        ASOAraOrderNumber: "",
        isARA: false,
        ARACurrentAccommodationType: "",
        ARACurrentMunicipality: "",
        Checksum: "",
      },
      residentInLocation: {
        localityId: "",
        isResident: false,
        localityName: [],
      },
      numberOfGroupedLocalities: 0,
      numberOfGroupedLocalitiesByCity: {},
    };
  },
  computed: {
    ...mapState({
      favourites: (state) => state.favourites[favouritesType],
      apartments: (state) => state.favouritesData[favouritesType],
      loadingApartments: (state) => !!state.favouritesDataLoading,
    }),
    vBindBase() {
      return {
        hero: {
          ...this.heroes[this.view],
          image: this.heroImage,
        },
        textBefore: this.textBefore,
        textAfter: this.textAfter,
        warningApartmentsBefore: this.warningApartmentsBefore,
        success: {
          content: this.view === 'deactivation-success' ? this.deactiovationSuccessContent : this.successContent,
          applyOther: {
            show:
              this.$store.state.favourites[
                applyOtherData.conditionFavouritesType
              ].length > 0,
            title: applyOtherData.title,
            content: this.view === 'deactivation-success' ? this.deactiovationSuccessContentApply : this.successContentApply,
            ctaText: applyOtherData.ctaText,
            color: applyOtherData.color,
          },
        },
        view: this.view,
        validations: this.$v,
        submitting: this.submitting,
        deactivating: this.deactivating,
        loading: this.loadingApartments,
        submitError: this.submitError,
        deactivateError: this.deactivateError,
        hasApartments: this.apartments.length > 0,
        isExistingApplication: this.isExistingApplication,
        existingApplicantIndexes: this.existingApplicantIndexes,
        modals: {
          loadPreset: {
            show: this.showModal.loadPreset,
            title: this.modalLoadPresetTitle,
            content: this.modalLoadPresetContent,
            textClose: this.modalLoadPresetClose,
          },
          confirmReset: {
            show: this.showModal.confirmReset,
          },
          prefillApplication: {
            show: this.showModal.prefillApplication,
            loading: this.modalLoading.prefillApplication,
            error: this.modalError.prefillApplication,
            errorMessage: this.modalErrorMessages.prefillApplication,
          },
          confirmDeactivation: {
            show: this.showModal.confirmDeactivation,
          },
        },
        loadApplicationDataFromAPI: this.loadApplicationDataFromAPI,
        disableFormFromWordPress: this.disableFormFromWordPress,
      };
    },
    vOnBase() {
      return {
        move: this.move,
        showModal: (modal, value) => {
          this.showModal[modal] = value;
        },
      };
    },
    vBindApartments() {
      return {
        interactive: this.view === "form",
        loading: this.loadingApartments,
        apartments:
          this.view === "success" ? this.appliedApartments : this.apartments,
        apartmentsCountMax: this.maxApartments,
        warningApartmentsAfter: this.warningApartmentsAfter,
        residentInLocation: this.residentInLocation,
      };
    },
    vOnApartments() {
      const remove = (uuid) => {
        this.$store.commit("removeFavourite", { uuid, type: favouritesType });
      };
      const selectedCheckbox = (obj) => {
        this.residentInLocation = {
          isResident: obj.value.length > 0,
          localityId: obj.localityId,
          localityName: obj.value,
        };
      };
      const groupedLocalities = (value) => {
        this.numberOfGroupedLocalities = value;
      };
      const groupedLocalitiesByCity = (value) => {
        this.numberOfGroupedLocalitiesByCity = value;
      };
      return {
        remove,
        selectedCheckbox,
        groupedLocalities,
        groupedLocalitiesByCity,
      };
    },
    vBindApplicants() {
      return {
        items: this.applicants,
        itemInit: this.createApplicant,
        vIter: this.$v.applicants.$each.$iter,
        preview: this.preview,
        isExistingApplication: this.isExistingApplication,
        existingApplicantIndexes: this.existingApplicantIndexes,
      };
    },
    computedLocalSave() {
      return this.getLocalSaveFromState(true);
    },
    isExistingApplication() {
      return !!this.form.Id;
    },
    preview() {
      return this.view === "preview";
    },
    // This is used to ensure that there is only one occurrence of each ssn.
    allSsns() {
      return [
        ...this.applicants.map((item) => item.Ssn),
        ...this.form.OtherResidents.map((item) => item.Ssn),
      ];
    },
  },
  watch: {
    favourites() {
      this.$store.dispatch("requestAndCleanUpFavourites");
    },
    // Copy the state of computedLocalSave to local storage as it changes.
    computedLocalSave: {
      handler(newLocalSave) {
        if (this.enableLocalStorageUpdate) {
          this.$store.commit("localSave/store", {
            type: favouritesType,
            localSave: newLocalSave,
          });
        }
      },
      deep: true,
    },
  },
  methods: {
    /**
     * Fetches the application data from the Laravel API (.. which calls the Node proxy, which then calls the Avain CRM).
     */
    async loadApplicationDataFromAPI(ssn, applicationGUID, applicationType) {
      //console.log("loadApplicationDataFromAPI", globals.urlIntegrationApi)
      this.modalLoading.prefillApplication = true;
      const res = (
        await axios.get(
          globals.urlIntegrationApi +
            `/public/application/?ssn=${ssn}&application_id=${applicationGUID}&application_type=${applicationType}`
        )
      ).data;
      //const res = (await axios.get('https://staging-api.avainasunnot.fi/api' + `/public/application/?ssn=${ssn}&application_id=${applicationGUID}&application_type=${applicationType}`)).data
      this.modalLoading.prefillApplication = false;

      /**
       * If the SSN matches with the applicant SSN, the application data is returned in the response. We then need to
       * reset the state and the localStorage and map the application data to them again.
       */
      if (res.success && res.success === true) {
        this.modalError.prefillApplication = false;
        this.modalErrorMessages.prefillApplication = "";
        window.localStorage.clear();
        //console.log("RESDATA", res.applicationData);
        await this.populateStateAndLocalStorageWithApplicationData(
          res.applicationData,
          applicationType
        );
        this.showModal.prefillApplication = false;

        // Need to know which applicants were originally in the fetched application because
        //  so we can prevent changing/removing second original applicant (part of checksum)
        Object.keys(this.$v.applicants.$each.$iter).forEach((index) => {
          if (this.$v.applicants.$each.$iter[index].Ssn.$model !== '') {
            this.existingApplicantIndexes[index] = true;
          }
        });

        return;
      }

      /**
       * If the SSN does not match with the applicant SSN, display the error message.
       */
      this.modalError.prefillApplication = true;
      this.modalErrorMessages.prefillApplication = res.message;
    },
    navigateAfterReset() {
      window.location.href = globals.urlHome;
    },
    move(to) {
      if (to === "preview") {
        if (this.$v.$invalid) {
          this.$v.$touch();
          return;
        }
        window.scrollTo(0, 0);
        this.view = "preview";
      } else if (to === "back") {
        window.scrollTo(0, 0);
        this.view = "form";
      } else if (to === "submit") {
        if (this.$v.$invalid) {
          this.$v.$touch();
          return;
        }
        //Disabling order number check since the fields have been set to be required
        // when needed this.checkOrderNumbers();
        this.submit();
      } else if (to === "reset") {
        this.resetLocalStorage();
        this.navigateAfterReset();
      } else if (to === "deactivateApplication") {
        this.deactivateApplication();
      } else if (to === "deactivationSuccess") {
        window.scrollTo(0, 0);
        this.view = "deactivation-success";
      } else if (to === "success") {
        window.scrollTo(0, 0);
        this.view = "success";
      } else if (to === "applyOther") {
        const state = this.getLocalSaveFromState();
        localStorage.setItem("applicationShared", JSON.stringify(state));
        window.location.href = applyOtherData.ctaUrl;
      }
    },
    orderNumberModalHandler() {
      // This is hacky but still better than using "prompt".
      // The eventlisteners are not removed, which is not optimal.

      this.modal = document.getElementsByClassName("ordernumber-modal");
      this.sendButton = document.getElementById("application-send");
      this.cancelButton = document.getElementById("application-cancel");

      if (this.modal.length > 0) {
        this.modal = this.modal[0];
      } else {
        return;
      }
      this.modal.classList.add("visible");
      if (!hasListeners) {
        this.sendButton.addEventListener("click", () => this.numberModalSend());
        this.cancelButton.addEventListener("click", () =>
          this.numberModalCancel()
        );
        hasListeners = true;
      }
    },
    numberModalSend() {
      this.modal.classList.remove("visible");
      this.submit();
    },
    numberModalCancel() {
      this.modal.classList.remove("visible");
    },
    checkOrderNumbers() {
      // Check if there is order number for every city and inform user.
      let missingOrderNumber = this.apartments
        .map(this.createFormApartment)
        .find((apartment) => apartment.ASOOrderNumber == "");

      if (missingOrderNumber === undefined || this.form.isARA) {
        this.submit();
      } else {
        this.orderNumberModalHandler();
      }
    },
    async deactivateApplication() {
      this.deactivating = true;
      this.deactivateError = false;
      // When deactivating only Type and application Id need to have input
      // other fields are required (or Digia suspects this) to exists
      const payload = {
        Type: "4",
        TotalWealth: null,
        ApplicationReason: null,
        NeedForApartmentStartDate: null,
        ApplicationReasonAdditionalInfo: null,
        CurrentApartmentInfo: {
          AccommodationType: null,
          ApartmentType: null,
          Area: null,
          NumberOfResidents: null,
          Rent: null,
          MaintenanceCharge: null
        },
        Id: this.form.Id,
        OtherResidents: [],
        AdditionalWishes: null,
        ASOAraOrderNumber: null,
        isARA: "false",
        ARACurrentAccommodationType: null,
        ARACurrentMunicipality: null,
        Checksum: null,
        SelectedApartments: [
          {
            Id: "b5a0ca3f-c127-446b-bc63-e9889d510378", // Dummy uuid (the field is required)
          }
        ],
        PrimaryApplicant: {
          FirstName: "-",
          LastName: "-",
          Ssn: "-",
          Address: null,
          PostalCode: null,
          City: null,
          usePrimaryApplicantAddress: null,
          Phone: null,
          PhoneCountryCode: null,
          ContactLanguage: null,
          Email: null,
          emailAgain: null,
          MarketingAllowed: "false",
          GrossIncome: null,
          Occupation: null,
          Employer: null,
          JobStartDate: null
        },
        OtherApplicants: [],
        IncomeArray: [
          {
            PrimaryApplicant: null
          }
        ]
      };
      console.log(payload);

      try {
        await axios.post(
          globals.urlIntegrationApi + "/public/application/process/",
          payload
        );
      } catch (error) {
        console.log("Caught error: ", error);
        this.deactivateError = true;
        this.deactivating = false;
        return;
      }

      console.log("done deactivating");
      this.deactivating = false;

      // Favourites and fields are cleared when application is submitted.
      this.appliedApartments = this.apartments;
      this.resetLocalStorage();
      this.move("deactivationSuccess");
    },
    async submit() {
      console.log("submit");
      const primaryApplicant = this.applicants[0];
      const applicants = this.applicants.map((applicant) => ({
        ...applicant,
        ...(applicant.usePrimaryApplicantAddress
          ? {
              Address: primaryApplicant.Address,
              PostalCode: primaryApplicant.PostalCode,
              City: primaryApplicant.City,
            }
          : {}),
        Ssn: applicant.Ssn.toUpperCase(),
        MarketingAllowed: applicant.MarketingAllowed ? "true" : "false",
      }));

      const payload = {
        ...this.form,
        isARA: this.form.isARA ? "true" : "false",
        SelectedApartments: this.apartments.map(this.createFormApartment),
        PrimaryApplicant: applicants.shift(),
        OtherApplicants: applicants,
      };

      this.submitting = true;
      this.submitError = false;

      console.log("submit payload", payload);
      try {
        await axios.post(
          globals.urlIntegrationApi + "/public/application/process/",
          payload
        );
        //await axios.post('https://staging-api.avainasunnot.fi/api/public/application/process/', payload);
        //await Promise.reject(30)  // for testing
      } catch (error) {
        this.submitError = true;
        this.submitting = false;
        return;
      }
      this.submitting = false;

      // Favourites and fields are cleared when application is submitted.
      this.appliedApartments = this.apartments;
      this.resetLocalStorage();
      this.move("success");
    },
    resetLocalStorage() {
      // Local storage update is disabled to make sure it stays empty.
      this.enableLocalStorageUpdate = false;
      this.$store.commit("emptyFavourites", { type: favouritesType });
      this.$store.commit("localSave/remove", { type: favouritesType });
    },
    // Create a single object from current state containing everything that is saved to local storage.
    getLocalSaveFromState(full = false) {
      const pick = (obj, properties) =>
        Object.assign(...properties.map((key) => ({ [key]: obj[key] })));
      const extend = createExtend(localSaveExtend);
      return {
        applicants: this.applicants.map((applicant) => ({
          ...pick(applicant, [
            "FirstName",
            "LastName",
            "Ssn",
            "AddedNewApplicant",
            "Address",
            "PostalCode",
            "City",
            "usePrimaryApplicantAddress",
            "Phone",
            "PhoneCountryCode",
            "ContactLanguage",
            "Email",
            "emailAgain",
            "MarketingAllowed",
          ]),
          ...(full ? extend("localSaveFromStateApplicant", applicant) : {}),
        })),
        form: {
          Id: full ? this.form.Id : null,
          AdditionalWishes: this.form.AdditionalWishes,
          ASOAraOrderNumber: this.form.ASOAraOrderNumber,
          isARA: this.form.isARA,
          Checksum: this.form.Checksum,
          OtherResidents: this.form.OtherResidents.map((otherResident) =>
            pick(otherResident, ["FirstName", "LastName", "Ssn"])
          ),
          ARACurrentAccommodationType: this.form.ARACurrentAccommodationType,
          ARACurrentMunicipality: this.form.ARACurrentMunicipality,
          ...(full ? extend("localSaveFromStateForm", this) : {}),
        },
        residentInLocation: this.residentInLocation,
        ...(full ? extend("localSaveFromState", this) : {}),
      };
    },
    setStateFromLocalSave(localSave, full = false) {
      this.applicants = localSave.applicants.map(this.createApplicant);
      this.form.OtherResidents = localSave.form.OtherResidents.map(
        this.createOtherResident
      );
      this.form.AdditionalWishes = localSave.form.AdditionalWishes;
      this.form.ASOAraOrderNumber = localSave.form.ASOAraOrderNumber;
      this.form.isARA = localSave.form.isARA;
      this.form.Checksum = localSave.form.Checksum;
      this.residentInLocation = localSave.residentInLocation;
      this.form.ARACurrentAccommodationType =
        localSave.form.ARACurrentAccommodationType;
      this.form.ARACurrentMunicipality = localSave.form.ARACurrentMunicipality;
      if (!full) {
        return;
      }
      this.form.Id = localSave.form.Id;
      const extend = createExtend(localSaveExtend);
      extend("stateFromLocalSave", this, localSave);
    },
    // !! DEPRECATED - The application data is not populated to the "preset" prop anymore.
    // Load state from API. There are a couple of optional hooks/mappers that are defined in presetMapExtend to
    // extend this logic for each component.
    async statePresetLoad() {
      const { preset } = this;
      const extend = createExtend(presetMapExtend);

      // For applicants, emailAgain field must be filled as it is not included in data.
      this.applicants = [
        preset.PrimaryApplicant,
        ...preset.OtherApplicants,
      ].map((applicant) =>
        this.createApplicant({
          ...applicant,
          emailAgain: applicant.Email || "",
          ...extend("mapApplicant", applicant),
        })
      );

      this.form.Id = preset.Id;
      this.form.OtherResidents = preset.OtherResidents.map(
        this.createOtherResident
      );
      this.form.AdditionalWishes = preset.AdditionalWishes;

      // A "hook" that can be implemented in the component.
      extend("hook", this);

      // Replace current favourites with apartments in preset data.
      this.$store.commit("emptyFavourites", { type: favouritesType });
      preset.SelectedApartments.forEach((apartment) => {
        this.$store.commit("addFavourite", {
          uuid: apartment.Id,
          type: favouritesType,
        });
      });
      await this.$store.dispatch("requestAndCleanUpFavourites");

      // A "hook" that can be implemented in the component.
      extend("hookApartmentsLoaded", this);
    },
    async populateStateAndLocalStorageWithApplicationData(
      applicationData,
      applicationType
    ) {
      const data = Object.assign({}, applicationData); // Clone the object just to make sure we don't cause any unwanted side effects
      const extend = createExtend(presetMapExtend);

      this.applicants = [data.PrimaryApplicant, ...data.OtherApplicants].map(
        (applicant) => {
          try {
            return this.createApplicant({
              ...applicant,
              emailAgain: applicant.Email || "",
              ...extend("mapApplicant", applicant),
            });
          } catch (err) {
            console.log(err);
          }
        }
      );

      this.$store.commit("emptyFavourites");
      data.SelectedApartments.forEach((apartment) => {
        this.$store.commit("addFavourite", {
          uuid: apartment.Id,
          type: favouritesType,
        });
      });
      await this.$store.dispatch("requestAndCleanUpFavourites");

      if (applicationType === "aso") {
        // When loading preset values from API, order numbers must be reassigned to cities. The state of
        // shareOrderNumber is not available in the preset.

        // 1. Remove all the apartments that we don't have the data for. It is possible (at least in theory) that
        // the data for a previously added apartment is no longer available.
        const selectedApartmentsFiltered = data.SelectedApartments.filter(
          (apartment) =>
            this.apartments
              .map((apartment) => apartment.mpc_asuntoid)
              .includes(apartment.Id)
        );

        // 2. Create a set of order numbers that contains all cityIds exactly once.
        const uniqueOrderNumbersWithApartmentIds =
          selectedApartmentsFiltered.reduce((accumulator, apartment) => {
            const hasOrderNumber = !!apartment.ASOOrderNumber;
            const cityId = this.apartments.find(
              (a) => a.mpc_asuntoid === apartment.Id
            ).mpc_kaupunki;
            return hasOrderNumber
              ? {
                  ...accumulator,
                  [cityId]: apartment.ASOOrderNumber,
                }
              : accumulator;
          }, {});

        // 3. Save the order number for each city
        Object.entries(uniqueOrderNumbersWithApartmentIds).forEach(
          ([cityId, orderNumber]) => {
            this.orderNumbers[cityId] = orderNumber.toString();
          }
        );
        // 4. Connect residency info to a locality (there can only be one)
        selectedApartmentsFiltered.forEach((apartment) => {
          if (apartment.Resident) {
            const localityId = this.apartments.find(
              (a) => a.mpc_asuntoid === apartment.Id
            ).mpc_kohdeid;
            //const address = this.apartments.find((a) => a.mpc_asuntoid === apartment.Id).building.mpc_katuosoite;
            const localityName = this.apartments.find(
              (a) => a.mpc_asuntoid === apartment.Id
            ).housing_locality.mpc_name;
            this.residentInLocation = {
              localityId: localityId,
              isResident: apartment.Resident,
              localityName: [localityName],
            };
          }
        });
        // 5. isARA
        this.form.isARA = data.isARA;
        this.form.ASOAraOrderNumber = data.ASOAraOrderNumber;
        this.form.ARACurrentMunicipality = data.ARACurrentMunicipality;
        this.form.ARACurrentAccommodationType =
          data.ARACurrentAccommodationType;
      } else if (applicationType === "owner-occupancy") {
        // When loading preset values from API, order numbers must be reassigned to cities. The state of
        // shareOrderNumber is not available in the preset.

        // 1. Remove all the apartments that we don't have the data for. It is possible (at least in theory) that
        // the data for a previously added apartment is no longer available.
        const selectedApartmentsFiltered = data.SelectedApartments.filter(
          (apartment) =>
            this.apartments
              .map((apartment) => apartment.mpc_asuntoid)
              .includes(apartment.Id)
        );

        // 2. Create a set of order numbers that contains all cityIds exactly once.
        const uniqueOrderNumbersWithApartmentIds =
          selectedApartmentsFiltered.reduce((accumulator, apartment) => {
            const hasOrderNumber = !!apartment.ASOOrderNumber;
            const cityId = this.apartments.find(
              (a) => a.mpc_asuntoid === apartment.Id
            ).mpc_kaupunki;
            return hasOrderNumber
              ? {
                  ...accumulator,
                  [cityId]: apartment.ASOOrderNumber,
                }
              : accumulator;
          }, {});

        // 3. Save the order number for each city
        Object.entries(uniqueOrderNumbersWithApartmentIds).forEach(
          ([cityId, orderNumber]) => {
            this.orderNumbers[cityId] = orderNumber.toString();
          }
        );
        // 4. Connect residency info to a locality (there can only be one)
        selectedApartmentsFiltered.forEach((apartment) => {
          if (apartment.Resident) {
            const localityId = this.apartments.find(
              (a) => a.mpc_asuntoid === apartment.Id
            ).mpc_kohdeid;
            //const address = this.apartments.find((a) => a.mpc_asuntoid === apartment.Id).building.mpc_katuosoite;
            const localityName = this.apartments.find(
              (a) => a.mpc_asuntoid === apartment.Id
            ).housing_locality.mpc_name;
            this.residentInLocation = {
              localityId: localityId,
              isResident: apartment.Resident,
              localityName: [localityName],
            };
          }
        });
        // 5. isARA
        this.form.isARA = data.isARA;
        this.form.ASOAraOrderNumber = data.ASOAraOrderNumber;
        this.form.ARACurrentMunicipality = data.ARACurrentMunicipality;
        this.form.ARACurrentAccommodationType =
          data.ARACurrentAccommodationType;
      } else if (applicationType === "rent") {
        this.form.TotalWealth = `${data.TotalWealth}`; // Cast to string
        this.form.ApplicationReason = data.ApplicationReason;
        this.form.NeedForApartmentStartDate = data.NeedForApartmentStartDate;
        this.form.CurrentApartmentInfo = data.CurrentApartmentInfo;
        this.form.ApplicationReasonAdditionalInfo =
          data.ApplicationReasonAdditionalInfo;
      }

      this.form.Id = data.Id;
      this.form.OtherResidents = data.OtherResidents.map(
        this.createOtherResident
      );
      this.form.AdditionalWishes = data.AdditionalWishes;
      extend("hook", this);

      this.form.Checksum = data.Checksum;
      this.enableLocalStorageUpdate = true;
      this.$store.commit("localSave/store", {
        type: favouritesType,
        localSave: this.computedLocalSave,
      });

      // A "hook" that can be implemented in the component.
      extend("hookApartmentsLoaded", this);
    },
    createApplicant: extendApplicantCreator(),
    createOtherResident: (presets = {}) => ({
      id: unique(),
      Ssn: "",
      LastName: "",
      FirstName: "",
      ...presets,
    }),
    createFormApartment: (apartment) => {
      return {
        Id: apartment.mpc_asuntoid,
        CampaignCode: apartment.ava_kampanjakoodi,
      };
    },
  },
  async beforeMount() {
    // Shared data in local storage is always removed.
    const localSaveSharedRaw = localStorage.getItem("applicationShared");
    if (localSaveSharedRaw) {
      localStorage.removeItem("applicationShared");
    }

    // A custom reset handler is used when a preset application is present to prevent application re-initializing itself
    // immediately after a reset by reloading the URL.
    applicationReset.setHandler((maybeResetLocalStorage) => {
      this.enableLocalStorageUpdate = false;
      maybeResetLocalStorage();
      this.navigateAfterReset();
    });

    // There are four different cases that can happen when initializing the application. Note that the order these are
    // checked has significance here: preset will *always* be prioritized over any of the other options etc.

    // 1) Display the "Continue your application" modal and load the existing application data to the form
    // When: User is coming in from a "Continue your application" link - i.e. "application" GET parameter exists in the URL
    // What: Present a modal. The modal asks for the SSN of the Primary Applicant. If the SSN the user enters matches the
    //       SSN in the application data, the server will return the application data. If not, the server will return an error message.
    // NOTE: the logic can continue to the next step even if the modal is loaded - the application state is ONLY reset with the API data
    //       if the user enters a valid SSN to the modal. The user also has an option to hide the modal.
    const queryParams = utils.generalHelpers.methods.getJSONFromURL();

    // "application" might cause problems in localhost, therefore also allow "applicationlocal"
    if (queryParams.application || queryParams.applicationlocal) {
      this.showModal.prefillApplication = true;
    }

    // 2) Application in progress
    // When: There is full application data (application[*]) present in local storage AND if preset data was passed as
    // props, its Id value matches the one in local storage.
    // What: All fields are initialized from the data in local storage.
    const localSave = this.$store.state.localSave[favouritesType];
    if (localSave) {
      this.setStateFromLocalSave(localSave, true);
      await this.$store.dispatch("requestAndCleanUpFavourites");
      this.enableLocalStorageUpdate = true;
      return;
    }

    // !! DISABLED 2021-05
    // 2) Load existing application
    // When: Preset data was passed as props.
    // What: Fields are loaded from preset. Favourites are overwritten with the ones in preset data. A local save is
    // created immediately.
    // if (this.preset) {
    //   await this.statePresetLoad();
    //   this.enableLocalStorageUpdate = true;
    //   this.$store.commit('localSave/store', {type: favouritesType, localSave: this.computedLocalSave});
    //   this.showModal.loadPreset = this.modalLoadPresetEnabled;
    //   return;
    // }

    // 3) Start from shared data
    // When: There is shared data (applicationShared) present in local storage.
    // What: Shared fields are initialized from the data in local storage. Application-specific fields are empty. A
    // local save is created immediately.
    if (localSaveSharedRaw) {
      const localSaveShared = JSON.parse(localSaveSharedRaw);
      this.setStateFromLocalSave(localSaveShared);
      await this.$store.dispatch("requestAndCleanUpFavourites");
      this.enableLocalStorageUpdate = true;
      this.$store.commit("localSave/store", {
        type: favouritesType,
        localSave: this.computedLocalSave,
      });
      return;
    }

    // 4) Fresh start
    // When: Default behaviour.
    // What: All fields are empty. Local save is not created immediately - only when the user actually fills something
    // in the application.
    await this.$store.dispatch("requestAndCleanUpFavourites");
    this.enableLocalStorageUpdate = true;
  },
});
